import { useEffect, useState } from 'react';
import { timeSince } from '../data/format.js';

/**
 * Run `load` when `deps` change. While a new load runs, the old data stays on
 * the page, so the layout does not jump.
 */
export function useAsync(load, deps) {
  const [state, setState] = useState({ data: undefined, error: null, loading: true });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(load)
      .then(
        (data) => { if (!cancelled) setState({ data, error: null, loading: false }); },
        (error) => { if (!cancelled) setState({ data: undefined, error, loading: false }); },
      );
    return () => { cancelled = true; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export function DateCell({ value }) {
  if (!value) return <span className="muted">—</span>;
  return <time dateTime={value} title={String(value).slice(0, 10)}>{timeSince(value)}</time>;
}

const ACTIVE_STATUS = new Set(['NORMAL', 'AKTIV']);
const WARN_STATUS = /UNDER|TVANG|KONKURS|REKONSTRUKTION/;

export function statusTone(status) {
  if (ACTIVE_STATUS.has(status)) return 'good';
  // A dissolved company is plain, except after a konkurs (the owner, 2026-09-25).
  if (status === 'OPLØSTEFTERKONKURS') return 'warn';
  if (WARN_STATUS.test(status) && !status.startsWith('OPLØST')) return 'warn';
  return 'plain';
}

export function StatusPill({ status }) {
  if (!status) return <span className="muted">—</span>;
  const tone = statusTone(status);
  const label = status.charAt(0) + status.slice(1).toLowerCase();
  return <span className={`pill ${tone}`}>{label}</span>;
}

export function ErrorAlert({ error }) {
  if (!error) return null;
  return <div className="alert error" role="alert">{error.message || String(error)}</div>;
}

// The JSON renders only when the block opens. The loader data already holds the
// document, so the server HTML does not get it a second time (B07).
export function JsonDetails({ value }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="json card" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Raw document</summary>
      {open && <pre>{JSON.stringify(value, null, 2)}</pre>}
    </details>
  );
}
